import React, { useState } from 'react';
import { BuyerRefundRow, PayMode } from 'services/reportsApi';

interface Props {
  rows: BuyerRefundRow[];
}

const fmtMoney = (raw: string): string => {
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

const PAY_MODE_LABEL: Record<PayMode, string> = {
  ach: 'ACH',
  check: 'Check',
  debit_card: 'Debit card',
  digital_check: 'Digital check',
};

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

const renderWithLinks = (text: string | null): React.ReactNode => {
  if (!text) return '—';
  const parts = text.split(URL_REGEX);
  return parts.map((part, i) =>
    URL_REGEX.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noreferrer noopener"
        className="text-primary-500 underline"
      >
        {part}
      </a>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
};

const ExpanderRow: React.FC<{ row: BuyerRefundRow; colSpan: number }> = ({ row, colSpan }) => (
  <tr className="bg-gray-50">
    <td colSpan={colSpan} className="px-4 py-3 text-13s text-gray-700">
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 md:grid-cols-3">
        <div>
          <span className="font-medium text-gray-500">Created:</span> {fmtDate(row.created_at)}
        </div>
        <div>
          <span className="font-medium text-gray-500">Approved:</span> {fmtDate(row.approved_at)}
        </div>
        <div>
          <span className="font-medium text-gray-500">Paid:</span> {fmtDate(row.paid_at)}
        </div>
        <div>
          <span className="font-medium text-gray-500">Check #:</span> {row.check_number ?? '—'}
        </div>
        <div>
          <span className="font-medium text-gray-500">Check mailed:</span>{' '}
          {fmtDate(row.check_mailed_on)}
        </div>
        <div>
          <span className="font-medium text-gray-500">Legacy notes:</span>{' '}
          {renderWithLinks(row.notes)}
        </div>
      </div>
    </td>
  </tr>
);

export const RefundsTable: React.FC<Props> = ({ rows }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const colSpan = 10;

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-separate border-spacing-0 text-14s">
        <thead>
          <tr className="bg-gray-100 text-left text-13s uppercase tracking-wide text-gray-600">
            <th className="px-4 py-2 font-medium">PayUp ID</th>
            <th className="px-4 py-2 font-medium">Batch #</th>
            <th className="px-4 py-2 font-medium">Vendor</th>
            <th className="px-4 py-2 font-medium">Property</th>
            <th className="px-4 py-2 text-right font-medium">Amount</th>
            <th className="px-4 py-2 font-medium">Mode</th>
            <th className="px-4 py-2 font-medium">Refunded On</th>
            <th className="px-4 py-2 font-medium">Refunded By</th>
            <th className="px-4 py-2 font-medium">Reason</th>
            <th className="px-4 py-2 font-medium" aria-label="Expand" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isExpanded = expanded.has(row.transfer_id);
            return (
              <React.Fragment key={row.transfer_id}>
                <tr className="border-b border-gray-200 bg-white hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-13s text-gray-700">
                    {row.payup_transfer_number}
                  </td>
                  <td className="px-4 py-3 font-mono text-13s text-gray-700">
                    {row.batch_number ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-900">{row.vendor_name ?? 'Unknown vendor'}</td>
                  <td className="px-4 py-3 text-gray-900">
                    {row.property_name ?? 'Unknown property'}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900">
                    {fmtMoney(row.amount)}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {PAY_MODE_LABEL[row.pay_mode] ?? row.pay_mode}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{fmtDate(row.refunded_at)}</td>
                  <td className="px-4 py-3 text-gray-700">{row.refunded_by_user_name ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-700">{renderWithLinks(row.refund_notes)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => toggle(row.transfer_id)}
                      className="text-13s text-primary-500 hover:underline"
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                    >
                      {isExpanded ? 'Hide' : 'Details'}
                    </button>
                  </td>
                </tr>
                {isExpanded && <ExpanderRow row={row} colSpan={colSpan} />}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default RefundsTable;
